'use client'

import Link from 'next/link'
import { FontAwesomeSvgIcon } from 'react-fontawesome-svg-icon'

import { faChartColumn } from '@fortawesome/pro-light-svg-icons'

import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import { Card } from '@infura/packages.ui/Card'
import { Text } from '@infura/packages.ui/Text'
import { Tooltip } from '@infura/packages.ui/Tooltip'

import { DailyCreditUsageChart } from 'components/dashboard/analytics-overview/DailyCreditUsageChart/DailyCreditUsageChart'

import { useUKSServicePlan } from 'hooks/useUKSServicePlan'

import { BILLING_PLAN_ROUTE, BILLING_USAGE_ROUTE } from 'lib/constants'
import { getOnCreditPlan } from 'lib/utils/creditPlan'

const baseTrackingProps = {
  location: 'Analytics Overview Usage Section'
}

export const UsageSection = () => {
  const { isLoading, servicePlan } = useUKSServicePlan()
  const isUsageEnabled = getOnCreditPlan(servicePlan)

  if (isLoading) {
    return null
  }

  const addonCreditLimit =
    (servicePlan?.limits?.creditUnits?.addon?.quantity ?? 0) * (servicePlan?.limits?.creditUnits?.addon?.credits ?? 0)
  const creditLimit = (servicePlan?.limits?.creditUnits?.threshold ?? 0) + addonCreditLimit

  return (
    <Card className='col-span-2'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <FontAwesomeSvgIcon className='text-text-default' icon={faChartColumn} size='sm' />
          {isUsageEnabled ? (
            <>
              <Text className='font-bold'>Daily Credit Usage</Text>
              <Tooltip
                content={
                  <div>
                    You can view your daily credit usage in relation to your daily credit quota limit. Daily credit
                    usage counts are reset at the beginning of each day. Additionally, you will find your top 5 methods
                    or API calls by credit usage.
                  </div>
                }
              />
            </>
          ) : (
            <>
              <Text className='font-bold'>Daily Request Usage</Text>
              <Tooltip
                className='z-1'
                content={
                  <div>
                    You can view your daily request usage in relation to your daily request quota limit. Daily request
                    usage counts are reset at the beginning of each day. Additionally, you will find your top 5 methods
                    or API calls by request usage.
                  </div>
                }
              />
            </>
          )}
        </div>
        {isUsageEnabled ? (
          <div className='flex flex-wrap gap-2 sm:gap-4'>
            <Link
              data-testid='daily-credit-usage-add-credits'
              href={BILLING_PLAN_ROUTE}
              onClick={() => {
                trackClickForSegmentAnalytics({
                  ...baseTrackingProps,
                  clickText: 'Add Credits',
                  type: 'Text'
                })
              }}
            >
              Add Credits
            </Link>
            <Link
              data-testid='daily-credit-usage-view-usage'
              href={BILLING_USAGE_ROUTE}
              onClick={() => {
                trackClickForSegmentAnalytics({
                  ...baseTrackingProps,
                  clickText: 'View Usage',
                  type: 'Text'
                })
              }}
            >
              View Usage
            </Link>
          </div>
        ) : (
          <Link
            href={BILLING_PLAN_ROUTE}
            onClick={() => {
              trackClickForSegmentAnalytics({
                ...baseTrackingProps,
                clickText: 'Manage Plan',
                type: 'Text'
              })
            }}
          >
            Manage Plan
          </Link>
        )}
      </div>
      <DailyCreditUsageChart creditLimit={creditLimit} isUsageEnabled={isUsageEnabled} />
    </Card>
  )
}
